import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Building2, GraduationCap, Heart, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const LICENSE_OPTIONS = [
  {
    name: 'Therappi® for Schools',
    icon: GraduationCap,
    description: 'Mental health support for students aged 13-17',
    features: [
      'Unlimited student licences',
      'Admin dashboard for school oversight',
      'Parental consent management',
      'Safe, age-appropriate content',
      'Emergency support tools',
      'Custom branding options',
      'Bulk licensing discounts',
      'Dedicated account manager',
    ],
    color: 'from-secondary to-accent',
  },
  {
    name: 'Therappi® for Business',
    icon: Building2,
    description: 'Employee wellness programmes for organisations',
    features: [
      'Unlimited employee access',
      'Anonymous usage analytics',
      'Mental health resource library',
      'Stress & burnout management',
      'Work-life balance tools',
      'Custom onboarding',
      'Flexible licensing tiers',
      'Dedicated support team',
    ],
    color: 'from-primary to-primary/80',
  },
  {
    name: 'NHS Therappi®',
    icon: Heart,
    description: 'Specialised support for NHS organisations',
    features: [
      'NHS-compliant data handling',
      'Integration with existing systems',
      'Staff mental health support',
      'Crisis intervention tools',
      'Shift work & burnout management',
      'Custom training materials',
      'Priority implementation',
      'Ongoing clinical support',
    ],
    color: 'from-muted to-muted/80',
  },
];

export default function EnterpriseLicensing() {
  const handleContact = (licenseName: string) => {
    const subject = encodeURIComponent(`Inquiry: ${licenseName}`);
    const body = encodeURIComponent(
      `Hello,\n\nI'm interested in learning more about ${licenseName}.\n\nOrganization Name: \nNumber of Users: \nPreferred Start Date: \n\nPlease provide more information about packages and pricing.\n\nThank you.`
    );
    
    window.location.href = `mailto:support@therappi.app?subject=${subject}&body=${body}`;
    
    toast.success('Opening email client...', {
      description: 'We\'ll respond within 24 hours',
    });
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          Enterprise Licensing
        </h1>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          We offer specialised licensing packages for schools, businesses, and NHS organisations. 
          Contact us for custom pricing and tailored solutions.
        </p>
      </div>

      {/* License Options */}
      <div className="grid md:grid-cols-3 gap-8">
        {LICENSE_OPTIONS.map((option) => {
          const Icon = option.icon;
          
          return (
            <Card key={option.name} className="relative overflow-hidden border-2 hover:shadow-xl transition-all">
              {/* Gradient Header */}
              <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${option.color}`} />
              
              <CardHeader className="text-center pt-8 pb-4">
                <div className={`mx-auto mb-4 p-4 rounded-full bg-gradient-to-r ${option.color} w-fit`}>
                  <Icon className="h-8 w-8 text-white" />
                </div>
                <CardTitle className="text-2xl">{option.name}</CardTitle>
                <CardDescription className="text-base pt-2">
                  {option.description}
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Features */}
                <ul className="space-y-3">
                  {option.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {/* Contact Button */}
                <Button 
                  onClick={() => handleContact(option.name)}
                  className="w-full gap-2"
                  size="lg"
                >
                  <Mail className="h-4 w-4" />
                  Contact Us for Pricing
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Additional Information */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="text-2xl text-center">Why Choose Enterprise Licensing?</CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Flexible Pricing
            </h3>
            <p className="text-muted-foreground">
              Custom packages tailored to your organization's size and needs, with significant bulk discounts.
            </p>
          </div>
          
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Dedicated Support
            </h3>
            <p className="text-muted-foreground">
              Priority support with a dedicated account manager to ensure smooth implementation.
            </p>
          </div>
          
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Data Security
            </h3>
            <p className="text-muted-foreground">
              Enterprise-grade security with customizable data retention and compliance options.
            </p>
          </div>
          
          <div className="space-y-3">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Custom Features
            </h3>
            <p className="text-muted-foreground">
              Tailored features, integrations, and branding options to match your organization's requirements.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Contact CTA */}
      <div className="text-center space-y-4 py-8">
        <h2 className="text-2xl font-bold">Ready to Get Started?</h2>
        <p className="text-muted-foreground">
          Contact us today to discuss your organization's needs and receive a custom quote.
        </p>
        <Button 
          onClick={() => handleContact('General Enterprise Inquiry')}
          size="lg"
          className="gap-2"
        >
          <Mail className="h-5 w-5" />
          Get in Touch
        </Button>
      </div>
    </div>
  );
}